import { useState } from 'react'
import Icon from '../components/Icon'
import { worx } from './client'
import { tw } from './strings'

/** The reasons the server accepts. See threads.ts `report`. */
const REASONS = ['scam', 'not_real', 'discrimination', 'harassment', 'spam', 'other'] as const

/**
 * Report something.
 *
 * ── Why this is a component and not three screens ───────────────────────────
 *
 * The endpoint and the administrator's queue were built first; nothing in the
 * app could reach them. That is not a missing nicety — Apple's rule 1.2 asks
 * for a way to flag objectionable content wherever it appears, and an app with
 * user-generated content and no report control is rejected. So it is one
 * control, placed on the three things a person can actually be harmed by: a
 * posting, an applicant's profile, and a conversation.
 *
 * ── The reasons are a closed list ───────────────────────────────────────────
 *
 * A free-text-only report is one an administrator has to read before they can
 * even sort it. The codes let the queue be triaged and counted; the box
 * underneath is where the actual story goes, and it is optional because
 * somebody reporting a scam at 11pm should not have to write an essay first.
 */
export default function Report({
  subjectType,
  subjectId,
  label,
}: {
  subjectType: 'job' | 'employer' | 'worker' | 'message' | 'service'
  subjectId: string
  /** What is being reported, in the button. "Report this job", not "Report". */
  label?: string
}) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<string | null>(null)
  const [detail, setDetail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function send() {
    if (!reason) return
    setBusy(true)
    setError(null)
    try {
      await worx.report({ subjectType, subjectId, reason, detail: detail.trim() || undefined })
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : tw('w.error.send'))
    } finally {
      setBusy(false)
    }
  }

  const close = () => {
    setOpen(false)
    // Reset only after it was sent: somebody who closed by accident should
    // find what they had typed still there.
    if (sent) { setSent(false); setReason(null); setDetail('') }
  }

  return (
    <>
      {/* Quiet, and never coral: reporting is available, not encouraged. */}
      <button className="link w-report-open" onClick={() => setOpen(true)}>
        <Icon name="alert" size={15} /> {label ?? tw('w.report.open')}
      </button>

      {open && (
        <>
          <div className="w-scrim" onClick={close} aria-hidden="true" />
          <div className="w-sheet" role="dialog" aria-modal="true" aria-label={tw('w.report.title')}>
            <header>
              <h2>{tw('w.report.title')}</h2>
              <button className="link" onClick={close}>{tw('w.feed.done')}</button>
            </header>

            {sent ? (
              <p className="w-ok">{tw('w.report.sent')}</p>
            ) : (
              <>
                <p className="muted small w-hint">{tw('w.report.hint')}</p>

                <div className="w-opts">
                  {REASONS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      className={reason === r ? 'w-opt on' : 'w-opt'}
                      aria-pressed={reason === r}
                      onClick={() => setReason(r)}
                    >
                      {tw(`w.report.reason.${r}` as never)}
                    </button>
                  ))}
                </div>

                <label>
                  {tw('w.report.detail')}
                  <textarea
                    rows={4}
                    value={detail}
                    maxLength={2000}
                    onChange={(e) => setDetail(e.target.value)}
                  />
                </label>

                {error && <p className="error">{error}</p>}

                <button className="primary" disabled={busy || !reason} onClick={() => void send()}>
                  <Icon name="mail" size={18} /> {busy ? '…' : tw('w.report.send')}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </>
  )
}
